import { useState, useEffect } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router'
import { useAuthStore } from '@/stores/auth.store'
import { EmailVerificationBanner } from '@/components/auth'
import { Logo } from './Logo'

// Icon components - all have aria-hidden since they're decorative
function HomeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
    </svg>
  )
}

function PencilSquareIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
    </svg>
  )
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
    </svg>
  )
}

function CogIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </svg>
  )
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
    </svg>
  )
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
    </svg>
  )
}

function ChatBubbleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z" />
    </svg>
  )
}

interface NavButtonProps {
  to: string
  icon: React.ReactNode
  label: string
  isActive: boolean
  onClick: () => void
  badge?: number
}

function NavButton({ icon, label, isActive, onClick, badge }: NavButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={isActive ? 'page' : undefined}
      aria-label={badge ? `${label}, ${badge} unread` : label}
      className={`flex-1 flex flex-col items-center justify-center relative ${
        isActive
          ? 'text-primary-600 bg-primary-50'
          : 'text-gray-500 active:bg-gray-100'
      }`}
    >
      <span className="pointer-events-none relative">
        {icon}
        {badge !== undefined && badge > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </span>
      <span className="pointer-events-none text-xs font-medium mt-1">{label}</span>
    </button>
  )
}

export function Layout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, accessToken, logout } = useAuthStore()
  const [unreadCount, setUnreadCount] = useState(0)

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/')

  // Fetch unread message count
  useEffect(() => {
    if (!user || !accessToken) return

    const fetchUnreadCount = async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_URL || ''}/api/messages/unread-count`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            credentials: 'include',
          }
        )
        if (response.ok) {
          const data = await response.json()
          setUnreadCount(data.unreadCount)
        }
      } catch (err) {
        console.error('Failed to fetch unread count:', err)
      }
    }

    fetchUnreadCount()

    // Poll every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000)
    return () => clearInterval(interval)
  }, [user, accessToken, location.pathname])

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-white pt-safe">
      {/* Header */}
      <header className="flex-none border-b border-gray-100 bg-white">
        <div className="flex h-10 items-center justify-between px-3">
          <Logo size="sm" />
          {user && (
            <button
              onClick={handleLogout}
              className="text-sm text-gray-500 active:text-gray-700"
              aria-label="Log out of your account"
            >
              Logout
            </button>
          )}
        </div>
      </header>

      {/* Persistent email verification banner for unverified parents */}
      {user?.role === 'parent' && !user.emailVerified && (
        <div className="flex-none px-4 py-2 bg-white">
          <EmailVerificationBanner />
        </div>
      )}

      {/* Main content - scrollable area */}
      <main className="flex-1 overflow-y-auto bg-gray-50">
        <Outlet />
        <div className="h-4" />
      </main>

      {/* Bottom navigation - part of flex layout, sits at bottom */}
      <nav className="flex-none bg-white border-t border-gray-200 pb-safe" aria-label="Main navigation">
        <div className="flex h-14" role="menubar">
          <NavButton
            to="/dashboard"
            icon={<HomeIcon className="h-7 w-7" />}
            label="Home"
            isActive={isActive('/dashboard')}
            onClick={() => navigate('/dashboard')}
          />

          {user?.role === 'childminder' && (
            <>
              <NavButton
                to="/attendance"
                icon={<CheckCircleIcon className="h-7 w-7" />}
                label="Attendance"
                isActive={isActive('/attendance')}
                onClick={() => navigate('/attendance')}
              />
              <NavButton
                to="/updates"
                icon={<PencilSquareIcon className="h-7 w-7" />}
                label="Updates"
                isActive={isActive('/updates')}
                onClick={() => navigate('/updates')}
              />
              <NavButton
                to="/calendar"
                icon={<CalendarIcon className="h-7 w-7" />}
                label="Calendar"
                isActive={isActive('/calendar')}
                onClick={() => navigate('/calendar')}
              />
              <NavButton
                to="/messages"
                icon={<ChatBubbleIcon className="h-7 w-7" />}
                label="Messages"
                isActive={isActive('/messages')}
                onClick={() => navigate('/messages')}
                badge={unreadCount}
              />
              <NavButton
                to="/settings"
                icon={<CogIcon className="h-7 w-7" />}
                label="Settings"
                isActive={isActive('/settings') || isActive('/children')}
                onClick={() => navigate('/settings')}
              />
            </>
          )}

          {user?.role === 'parent' && (
            <>
              <NavButton
                to="/timeline"
                icon={<ClockIcon className="h-7 w-7" />}
                label="Timeline"
                isActive={isActive('/timeline')}
                onClick={() => navigate('/timeline')}
              />
              <NavButton
                to="/my-calendar"
                icon={<CalendarIcon className="h-7 w-7" />}
                label="Calendar"
                isActive={isActive('/my-calendar')}
                onClick={() => navigate('/my-calendar')}
              />
              <NavButton
                to="/messages"
                icon={<ChatBubbleIcon className="h-7 w-7" />}
                label="Messages"
                isActive={isActive('/messages')}
                onClick={() => navigate('/messages')}
                badge={unreadCount}
              />
              <NavButton
                to="/settings"
                icon={<CogIcon className="h-7 w-7" />}
                label="Settings"
                isActive={isActive('/settings')}
                onClick={() => navigate('/settings/notifications')}
              />
            </>
          )}
        </div>
      </nav>
    </div>
  )
}

export default Layout
